import type { Maybe } from '@adornis/base/utilTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { Weekday } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { KPF } from '../../master-data-management/kpf/entities/KPF.js';
import { Mitarbeiter } from '../../master-data-management/users/entities/Mitarbeiter.js';
import { WorkshopAPI } from '../../workshops/api/WorkshopAPI.js';
import { Workshop } from '../../workshops/entities/Workshop.js';
import { WorkshopAttendanceStatus } from '../../workshops/entities/WorkshopAttendanceStatus.js';
import { WorkshopFilter } from '../../workshops/entities/WorkshopFilter.js';

export enum CapacityRequestType {
  LEKO = 'LeKo',
  SCHUKO = 'SchuKo',
}
export enum CapacityRequestStatus {
  NEW = 'neu',
  IN_PROGRESS = 'in Arbeit',
  TRIAL_DAY = 'Schnuppertag',
  HIRED = 'eingestellt',
}

@Entity()
export class CapacityRequest extends MongoEntity {
  static override _class = 'CapacityRequest';
  static override _collectionName = 'capacity-requests';

  @Field(() => Boolean)
  public highPriority: Maybe<boolean>;

  @validate(nonOptional())
  @Field(() => String)
  public type: Maybe<CapacityRequestType>;

  @validate(nonOptional())
  @Field(() => DateTime)
  public fromWhen: Maybe<DateTime>;

  @validate(nonOptional())
  @Field(() => [String])
  public weekdays: Maybe<Weekday[]>;

  @validate(nonOptional())
  @Field(() => String)
  public status: Maybe<CapacityRequestStatus>;

  @Field(() => String)
  public specialSkills: Maybe<string>;

  @Field(() => String)
  public comment: Maybe<string>;

  @Field(() => String)
  public vorname: Maybe<string>;

  @Field(() => String)
  public nachname: Maybe<string>;

  @Field(() => String)
  public telefonBewerber: Maybe<string>;

  @Field(() => String)
  public bewerbungslink: Maybe<string>;

  @Field(() => DateTime)
  public schnuppertagDatum: Maybe<DateTime>;

  @Field(() => DateTime)
  public datumVersandWillkommensmail: Maybe<DateTime>;

  @Field(() => DateTime)
  public datumErsteTeilnahmeLernfoerderung: Maybe<DateTime>;

  @Field(() => DateTime)
  public datumFeedbackunterlagenErhalten: Maybe<DateTime>;

  @Field(() => DateTime)
  public datumFeedbackgespraech: Maybe<DateTime>;

  @Field(() => DateTime, { default: v => v ?? DateTime.now() })
  public createdAt: Maybe<DateTime>;

  @validate(nonOptional())
  @Field(() => String)
  public createdByID: Maybe<string>;

  @DataLoadedField(() => Mitarbeiter, ({ createdByID }) => createdByID)
  public createdBy: Maybe<Mitarbeiter>;

  @Field(() => String)
  public editedByID: Maybe<string>;

  @DataLoadedField(() => Mitarbeiter, ({ editedByID }) => editedByID)
  public editedBy: Maybe<Mitarbeiter>;

  @Field(() => DateTime, { default: v => v ?? DateTime.now() })
  public editedAt: Maybe<DateTime>;

  @validate(nonOptional())
  @Field(() => String)
  public KPFID: Maybe<string>;

  @DataLoadedField(() => KPF, ({ KPFID }) => KPFID)
  public KPF: Maybe<KPF>;

  @Field(() => String)
  public linkedUserID: Maybe<string>;

  @DataLoadedField(() => Mitarbeiter, ({ linkedUserID }) => linkedUserID)
  public linkedUser: Maybe<Mitarbeiter>;

  @Field(() => DateTime, {
    resolve: function (this: CapacityRequest) {
      return async () => {
        if (!this.linkedUserID) {
          return null;
        }

        const workshopID = (await loadMongoData(Workshop, { title: { $regex: 'chancenspirit', $options: 'i' } }))[0]
          ?._id;
        if (!workshopID) {
          return null;
        }

        const attend = await firstValueFrom(
          WorkshopAPI.subscribeAllAttendanceSimple(
            0,
            1,
            new WorkshopFilter({ userID: this.linkedUserID, workshopID, status: WorkshopAttendanceStatus.ATTENDED }),
          )({ date: 1 }),
        );

        return attend[0]?.date;
      };
    },
  })
  public datumChancenSpirit: Maybe<DateTime>;

  @Field(() => DateTime, {
    resolve: function (this: CapacityRequest) {
      return async () => {
        if (!this.linkedUserID) {
          return null;
        }

        const workshopID = (
          await loadMongoData(Workshop, { title: { $regex: 'einführung lernkaskade', $options: 'i' } })
        )[0]?._id;
        if (!workshopID) {
          return null;
        }

        const attend = await firstValueFrom(
          WorkshopAPI.subscribeAllAttendanceSimple(
            0,
            1,
            new WorkshopFilter({ userID: this.linkedUserID, workshopID, status: WorkshopAttendanceStatus.ATTENDED }),
          )({ date: 1 }),
        );

        return attend[0]?.date;
      };
    },
  })
  public datumEinfuehrungLKK: Maybe<DateTime>;
}
